<template>
  <div class="oa-approve-page">
    <div class="page-header-card">
      <div>
        <div class="page-title">{{ viewOnly ? '流程详情' : '流程审批' }}</div>
        <div class="page-subtitle">OA 工作台 / {{ viewOnly ? '查看详情' : '待办任务' }}</div>
      </div>
      <div class="page-actions">
        <button class="ghost-btn" @click="goTaskList">返回列表</button>
      </div>
    </div>

    <div class="summary-card">
      <div class="summary-title">[{{ taskInfo.isLowCodeFlow ? 'LF' : 'DIY' }}] {{ taskInfo.processTypeName || taskInfo.formCode || '流程任务' }}</div>
      <div class="summary-grid">
        <div class="summary-item">
          <span>流程编号：</span>
          <strong>{{ hiddenCode(taskInfo.processNumber) }}</strong>
        </div>
        <div class="summary-item">
          <span>流程描述：</span>
          <strong>{{ taskInfo.description || '暂无描述' }}</strong>
        </div>
        <div class="summary-item">
          <span>创建时间：</span>
          <strong>{{ formatTime(taskInfo.createTime) }}</strong>
        </div>
        <div class="summary-item">
          <span>更新时间：</span>
          <strong>{{ formatTime(taskInfo.runTime) }}</strong>
        </div>
      </div>
    </div>

    <div class="detail-card">
      <div class="tabs">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          class="tab-btn"
          :class="{ active: activeTab === tab.value }"
          @click="activeTab = tab.value"
        >
          {{ tab.label }}
        </button>
      </div>

      <div v-if="loading" class="loading-state">加载审批资源中...</div>

      <div v-else-if="activeTab === 'form'" class="tab-panel">
        <div v-if="formDisplayItems.length" class="form-grid">
          <div v-for="item in formDisplayItems" :key="item.key" class="form-item">
            <label class="field-label">
              {{ item.label }}
              <span v-if="item.required" class="required">*</span>
            </label>

            <textarea
              v-if="item.displayType === 'textarea'"
              :value="item.value"
              class="field-textarea"
              disabled
            ></textarea>

            <input
              v-else
              :value="item.value"
              class="field-input"
              type="text"
              disabled
            />
          </div>
        </div>

        <div v-else class="empty-state">暂无表单数据</div>
      </div>

      <div v-else-if="activeTab === 'records'" class="tab-panel">
        <div v-if="approvalRecords.length" class="record-table">
          <div class="record-row record-head">
            <span>流程环节</span>
            <span>执行人</span>
            <span>操作</span>
            <span>审批意见</span>
            <span>处理时间</span>
          </div>
          <div v-for="(record, index) in approvalRecords" :key="index" class="record-row">
            <span>{{ record.nodeName || '发起' }}</span>
            <span>{{ record.executorName || '-' }}</span>
            <span>
              <em class="record-status" :class="getRecordClass(record.operation)">{{ record.operation || '未处理' }}</em>
            </span>
            <span>{{ record.comment || '-' }}</span>
            <span>{{ formatTime(record.processTime) }}</span>
          </div>
        </div>
        <div v-else class="empty-state">暂无审批记录</div>
      </div>

      <div v-else class="tab-panel">
        <div v-if="flowNodes.length" class="flow-panel">
          <div class="flow-zoom-controls">
            <button class="zoom-btn" @click="zoomOut">−</button>
            <span class="zoom-text">{{ flowZoomText }}</span>
            <button class="zoom-btn" @click="zoomIn">＋</button>
            <button class="zoom-btn reset-btn" @click="resetZoom">⟳</button>
          </div>

          <div class="flow-preview" :style="flowStyle">
            <div v-for="(node, index) in flowNodes" :key="node.id || index" class="flow-node-wrap">
              <div class="flow-node" :class="node.statusClass">
                <div class="node-title">{{ node.name }}</div>
                <div class="node-meta">{{ node.approvers || node.typeName || '未配置处理人' }}</div>
              </div>
              <div v-if="index < flowNodes.length - 1" class="flow-line"></div>
            </div>
            <div class="flow-end-wrap">
              <div class="flow-end-line"></div>
              <div class="flow-end-dot"></div>
              <div class="flow-end-text">流程结束</div>
            </div>
          </div>
        </div>
        <div v-else class="empty-state">暂无流程预览数据</div>
      </div>
    </div>

    <div v-if="!viewOnly && approvalButtons.length" class="approval-bar">
      <button
        v-for="btn in approvalButtons"
        :key="btn.value"
        class="approval-btn"
        :class="getButtonClass(btn.value)"
        :disabled="submitting"
        @click="handleApproval(btn)"
      >
        {{ btn.label }}
      </button>
    </div>

    <div v-if="transferDialogVisible" class="dialog-mask">
      <div class="transfer-dialog">
        <div class="dialog-title">转办</div>
        <label class="field-label">选择转办人</label>
        <select v-model="selectedTransferId" class="field-select">
          <option value="">请选择转办人</option>
          <option v-for="user in transferCandidates" :key="user.id" :value="user.id">
            {{ user.account ? user.name + '(' + user.account + ')' : user.name }}
          </option>
        </select>

        <label class="field-label dialog-label">审批意见</label>
        <textarea v-model="transferComment" class="field-textarea"></textarea>

        <div class="dialog-actions">
          <button class="ghost-btn" @click="closeTransferDialog">取消</button>
          <button class="approval-btn btn-transfer" :disabled="submitting || !selectedTransferId" @click="confirmTransfer">确定转办</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchApprovalDetail,
  fetchApprovalRecords,
  fetchFlowPreview,
  fetchFormConfig,
  submitApprovalTask,
} from '../api/oa';

const APPROVAL_BUTTONS = [
  { value: 3, label: '同意' },
  { value: 4, label: '不同意' },
  { value: 18, label: '退回修改' },
  { value: 21, label: '转办' },
];

const SUPPORTED_APPROVAL_TYPES = [3, 4, 18, 21];

export default {
  name: 'OAApprove',
  data() {
    return {
      taskInfo: {},
      viewOnly: false,
      loading: false,
      submitting: false,
      activeTab: 'form',
      tabs: [
        { value: 'form', label: '表单信息' },
        { value: 'records', label: '审批记录' },
        { value: 'flow', label: '流程预览' },
      ],
      formData: null,
      lfFields: {},
      widgets: [],
      formModel: {},
      approvalButtons: [],
      approvalRecords: [],
      flowNodes: [],
      rawFlowData: null,
      transferCandidates: [],
      transferDialogVisible: false,
      selectedTransferId: '',
      transferComment: '转办',
      flowScale: 0.7,
    };
  },
  computed: {
    formDisplayItems() {
      const widgetNames = new Set();
      const widgetItems = (this.widgets || [])
        .filter(widget => widget && widget.options && widget.options.name)
        .map((widget) => {
          const fieldName = widget.options.name;
          widgetNames.add(fieldName);
          return {
            key: widget.key || widget.id || fieldName,
            label: this.getWidgetLabel(widget),
            required: !!widget.options.required,
            displayType: widget.type === 'textarea' ? 'textarea' : 'input',
            value: this.getFieldDisplayValue(widget),
          };
        });

      const rawItems = Object.keys(this.lfFields || {})
        .filter(key => !['approversList', 'approversValid'].includes(key) && !widgetNames.has(key))
        .map(key => ({
          key: `raw-${key}`,
          label: this.getRawFieldLabel(key),
          required: false,
          displayType: 'input',
          value: this.stringifyValue(this.lfFields[key]),
        }));

      return this.ensureDayCountItem([...widgetItems, ...rawItems]);
    },
    rawFieldEntries() {
      return Object.keys(this.lfFields || {})
        .filter(key => !['approversList', 'approversValid'].includes(key))
        .map(key => ({
          key,
          value: this.stringifyValue(this.lfFields[key]),
        }));
    },
    flowZoomText() {
      return `${Math.round(this.flowScale * 100)}%`;
    },
    flowStyle() {
      const scale = this.flowScale;
      return {
        '--flow-node-width': `${360 * scale}px`,
        '--flow-node-min-height': `${118 * scale}px`,
        '--flow-title-height': `${38 * scale}px`,
        '--flow-title-padding': `${22 * scale}px`,
        '--flow-title-font-size': `${16 * scale}px`,
        '--flow-meta-min-height': `${78 * scale}px`,
        '--flow-meta-padding-y': `${14 * scale}px`,
        '--flow-meta-padding-x': `${18 * scale}px`,
        '--flow-meta-font-size': `${17 * scale}px`,
        '--flow-line-width': `${Math.max(2, 3 * scale)}px`,
        '--flow-line-height': `${54 * scale}px`,
        '--flow-arrow-size': `${9 * scale}px`,
        '--flow-arrow-height': `${13 * scale}px`,
        '--flow-dot-size': `${34 * scale}px`,
        '--flow-end-font-size': `${16 * scale}px`,
        '--flow-end-margin': `${14 * scale}px`,
      };
    },
  },
  created() {
    this.initFromRoute();
  },
  methods: {
    initFromRoute() {
      const paramsStr = this.$route.query.params;
      if (!paramsStr) {
        this.$message.error('缺少审批任务参数');
        this.goTaskList();
        return;
      }

      try {
        this.taskInfo = JSON.parse(decodeURIComponent(paramsStr)) || {};
        this.viewOnly = !!this.taskInfo.viewOnly;
      } catch (e) {
        console.error('解析审批任务参数失败', e);
        this.$message.error('审批任务参数异常');
        this.goTaskList();
        return;
      }

      if (!this.taskInfo.formCode || !this.taskInfo.processNumber) {
        this.$message.error('审批任务参数不完整');
        this.goTaskList();
        return;
      }

      this.loadPageData();
    },
    async loadPageData() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadFormData(),
          this.loadApprovalRecords(),
          this.loadFlowPreview(),
        ]);
      } finally {
        this.loading = false;
      }
    },
    async loadFormData() {
      try {
        const res = await fetchApprovalDetail(this.taskInfo);
        if (res && res.code === 200) {
          const payload = this.normalizeApprovalPayload(res.data);
          this.applyApprovalPayload(payload);
          if (!this.formData && this.taskInfo.formCode) {
            await this.loadFallbackFormConfig();
          }
          this.widgets = this.collectFormWidgets((this.formData && this.formData.widgetList) || []);
          this.initFormModel();
          this.approvalButtons = this.resolveApprovalButtons(payload);
        } else {
          this.$message.error((res && res.msg) || '获取表单数据失败');
          await this.loadFallbackFormConfig();
        }
      } catch (e) {
        console.error('加载审批表单失败', e);
        this.$message.error('加载审批表单失败');
        await this.loadFallbackFormConfig();
      }
    },
    async loadFallbackFormConfig() {
      try {
        const res = await fetchFormConfig(this.taskInfo.formCode);
        if (res && res.code === 200 && res.data) {
          this.formData = this.parseMaybeJson(res.data);
          this.widgets = this.collectFormWidgets((this.formData && this.formData.widgetList) || []);
          this.initFormModel();
        }
      } catch (e) {
        console.warn('兜底获取表单配置失败', e);
      }
    },
    async loadApprovalRecords() {
      try {
        const res = await fetchApprovalRecords(this.taskInfo.processNumber);
        if (res && res.code === 200) {
          this.approvalRecords = this.mapApprovalRecords(res.data || []);
        } else {
          console.warn('审批记录加载失败', res);
        }
      } catch (e) {
        console.warn('加载审批记录异常', e);
      }
    },
    async loadFlowPreview() {
      try {
        const res = await fetchFlowPreview(this.taskInfo);
        if (res && res.code === 200) {
          this.rawFlowData = res.data || null;
          this.flowNodes = this.extractFlowNodes(this.rawFlowData);
          this.transferCandidates = this.extractTransferCandidates(this.rawFlowData);
        } else {
          console.warn('流程预览加载失败', res);
        }
      } catch (e) {
        console.warn('加载流程预览异常', e);
      }
    },
    normalizeApprovalPayload(payload) {
      let responseData = this.parseMaybeJson(payload);
      if (responseData && !responseData.lfFormData && (responseData.data || responseData.rows)) {
        const inner = responseData.data || responseData.rows;
        if (inner && inner.lfFormData) {
          responseData = inner;
        } else if (Array.isArray(inner) && inner.length > 0 && inner[0] && inner[0].lfFormData) {
          responseData = inner[0];
        }
      }
      return responseData || {};
    },
    applyApprovalPayload(payload) {
      if (payload && payload.lfFormData) {
        this.formData = this.parseMaybeJson(payload.lfFormData);
      }
      if (payload && payload.lfFields) {
        this.lfFields = this.parseMaybeJson(payload.lfFields) || {};
      }
    },
    resolveApprovalButtons(payload) {
      if (this.viewOnly) {
        return [];
      }

      const processRecordInfo = (payload && payload.processRecordInfo) || {};
      const pcButtons = processRecordInfo.pcButtons || {};
      const auditButtons = Array.isArray(pcButtons.audit) ? pcButtons.audit : [];
      const mapped = auditButtons
        .map(item => ({
          value: Number(item.buttonType),
          label: item.name || this.getActionText(Number(item.buttonType)),
        }))
        .filter(item => SUPPORTED_APPROVAL_TYPES.includes(item.value));

      const uniqueMap = {};
      const uniqueList = mapped.filter((item) => {
        if (uniqueMap[item.value]) return false;
        uniqueMap[item.value] = true;
        return true;
      });

      if (uniqueList.length) {
        return uniqueList.sort((a, b) => a.value - b.value);
      }

      return APPROVAL_BUTTONS;
    },
    collectFormWidgets(widgetList, result = []) {
      if (!Array.isArray(widgetList)) {
        return result;
      }

      widgetList.forEach((widget) => {
        if (!widget) return;
        if (widget.options && widget.options.name) {
          result.push(widget);
        }
        if (Array.isArray(widget.widgetList)) {
          this.collectFormWidgets(widget.widgetList, result);
        }
        if (Array.isArray(widget.cols)) {
          widget.cols.forEach(col => this.collectFormWidgets((col && col.widgetList) || [], result));
        }
        if (Array.isArray(widget.rows)) {
          widget.rows.forEach((row) => {
            const cols = (row && row.cols) || [];
            cols.forEach(cell => this.collectFormWidgets((cell && cell.widgetList) || [], result));
          });
        }
        if (Array.isArray(widget.tabs)) {
          widget.tabs.forEach(tab => this.collectFormWidgets((tab && tab.widgetList) || [], result));
        }
      });

      return result;
    },
    initFormModel() {
      const nextModel = {};
      (this.widgets || []).forEach((widget) => {
        if (!widget.options || !widget.options.name) return;
        const fieldName = widget.options.name;
        if (this.lfFields[fieldName] !== undefined && this.lfFields[fieldName] !== null) {
          nextModel[fieldName] = this.lfFields[fieldName];
        } else if (widget.options.defaultValue !== undefined && widget.options.defaultValue !== null) {
          nextModel[fieldName] = widget.options.defaultValue;
        } else {
          nextModel[fieldName] = '';
        }
      });
      this.formModel = nextModel;
    },
    mapApprovalRecords(rawRecords) {
      if (!Array.isArray(rawRecords)) return [];
      return rawRecords.map((record, index) => {
        const taskName = this.cleanDisplayText(record.taskName);
        const isEnd = taskName === '流程结束';
        return {
          nodeName: taskName || this.getDefaultNodeName(index),
          executorName: isEnd ? ' ' : this.cleanDisplayText(record.verifyUserName),
          operation: isEnd ? '结束' : (record.verifyStatusName == null ? '未处理' : record.verifyStatusName),
          comment: record.verifyDesc || '',
          processTime: record.verifyDate || record.processTime || record.createTime || record.updateTime || '',
        };
      });
    },
    extractFlowNodes(flowData) {
      const list = Array.isArray(flowData && flowData.bpmnNodeList) ? flowData.bpmnNodeList : [];
      const beforeIds = (flowData && Array.isArray(flowData.beforeNodeIds)) ? flowData.beforeNodeIds.map(String) : [];
      const afterIds = (flowData && Array.isArray(flowData.afterNodeIds)) ? flowData.afterNodeIds.map(String) : [];
      const currentNodeId = flowData && flowData.currentNodeId !== undefined ? String(flowData.currentNodeId) : '';

      return list.map((node, index) => {
        const nodeId = String(node.nodeId || node.id || index);
        const nodeName = node.nodeName || node.nodeDisplayName || this.getDefaultNodeName(index);
        const nodeType = Number(node.nodeType);
        const isCurrent = currentNodeId && nodeId === currentNodeId;
        const isPassed = beforeIds.includes(nodeId);
        const isFuture = afterIds.includes(nodeId);
        const isApprover = [4, 7].includes(nodeType) || /审核|审批/.test(String(nodeName || ''));
        return {
          id: nodeId,
          name: nodeName,
          typeName: this.getNodeTypeName(node.nodeType),
          approvers: this.getNodeApprovers(node),
          statusClass: [
            isCurrent ? 'is-current' : isPassed ? 'is-passed' : isFuture ? 'is-future' : 'is-normal',
            (Number(node.nodeType) === 1 || index === 0) ? 'is-start' : '',
            isApprover ? 'is-approver' : '',
          ].filter(Boolean).join(' '),
        };
      });
    },
    getNodeApprovers(node) {
      const params = (node && node.params) || {};
      const list = Array.isArray(params.assigneeList) ? params.assigneeList : [];
      const approvers = list
        .map(item => item.assigneeName || item.elementName || item.name || item.userName)
        .filter(Boolean)
        .join('、');
      if (!approvers) {
        return '';
      }
      return Number(node && node.nodeType) === 4 ? `指定人员:${approvers}` : approvers;
    },
    extractTransferCandidates(flowData) {
      const currentUser = this.getCurrentOaUser();
      const currentUserId = String(currentUser.userId || '');
      const currentNodeId = String((flowData && flowData.currentNodeId) || this.taskInfo.currentNodeId || '');
      const nodeList = Array.isArray(flowData && flowData.bpmnNodeList) ? flowData.bpmnNodeList : [];
      const currentNodes = nodeList.filter(node => String((node && node.nodeId) || '') === currentNodeId);
      const sourceNodes = currentNodes.length ? currentNodes : nodeList.filter(node => Number(node && node.nodeType) === 4);

      const candidates = [];
      sourceNodes.forEach((node) => {
        const assigneeList = Array.isArray(node && node.params && node.params.assigneeList)
          ? node.params.assigneeList
          : [];
        assigneeList.forEach((item) => {
          candidates.push({
            id: String(item.assignee || item.assigneeId || item.userId || item.id || ''),
            name: item.assigneeName || item.elementName || item.name || item.userName || '',
            account: item.account || item.userName || item.assigneeName || item.elementName || item.name || '',
          });
        });
      });

      const uniqueMap = {};
      return candidates.filter((item) => {
        if (!item.id || !item.name || item.id === currentUserId || uniqueMap[item.id]) {
          return false;
        }
        uniqueMap[item.id] = true;
        return true;
      });
    },
    getCurrentOaUser() {
      try {
        const raw = localStorage.getItem('ruoyi_user_info');
        const userInfo = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return {
          userId: userInfo && userInfo.userId,
          userName: userInfo && (userInfo.userName || userInfo.nickName),
        };
      } catch (e) {
        return {};
      }
    },
    getNodeTypeName(type) {
      const map = {
        1: '发起人',
        3: '条件',
        4: '审核人',
        6: '抄送人',
        7: '并行审批',
      };
      return map[type] || '流程节点';
    },
    async handleApproval(btn) {
      if (this.submitting) return;
      if (Number(btn.value) === 21) {
        this.openTransferDialog();
        return;
      }

      const actionText = btn.label || this.getActionText(btn.value);
      const defaultComment = this.getActionText(btn.value);

      try {
        const promptRes = await this.$prompt('请输入审批意见', actionText, {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputValue: defaultComment,
        });

        await this.submitApproval(btn.value, (promptRes && promptRes.value) || defaultComment);
      } catch (e) {
        if (e !== 'cancel' && e !== 'close') {
          console.error('审批操作异常', e);
          this.$message.error((e && e.message) || '审批操作失败');
        }
      }
    },
    openTransferDialog() {
      if (!this.transferCandidates.length && this.rawFlowData) {
        this.transferCandidates = this.extractTransferCandidates(this.rawFlowData);
      }
      if (!this.transferCandidates.length) {
        this.$message.warning('当前节点暂无可转办人员');
        return;
      }

      this.selectedTransferId = this.transferCandidates[0].id;
      this.transferComment = '转办';
      this.transferDialogVisible = true;
    },
    closeTransferDialog() {
      if (this.submitting) return;
      this.transferDialogVisible = false;
      this.selectedTransferId = '';
      this.transferComment = '转办';
    },
    async confirmTransfer() {
      const currentUser = this.getCurrentOaUser();
      const transferUser = this.transferCandidates.find(item => item.id === this.selectedTransferId);
      if (!currentUser.userId || !currentUser.userName) {
        this.$message.error('当前 OA 用户信息缺失，请重新登录');
        return;
      }
      if (!transferUser) {
        this.$message.warning('请选择转办人员');
        return;
      }

      await this.submitApproval(21, this.transferComment || '转办', {
        userInfos: [
          { id: String(currentUser.userId), name: currentUser.userName },
          { id: String(transferUser.id), name: transferUser.name },
        ],
      });
      this.closeTransferDialog();
    },
    async submitApproval(operationType, comment, extra = {}) {
      this.submitting = true;
      try {
        const res = await submitApprovalTask(this.taskInfo, {
          approvalComment: comment,
          operationType,
          lfFields: this.lfFields || {},
          extra,
        });
        if (res && res.code === 200) {
          this.$message.success('审批成功');
          this.$router.replace({
            path: '/home/oa/tasks',
            query: { mode: 'todo' },
          });
        } else {
          this.$message.error((res && (res.msg || res.errMsg)) || '审批失败');
        }
      } catch (e) {
        console.error('审批提交失败', e);
        this.$message.error((e && e.message) || '审批提交失败');
      } finally {
        this.submitting = false;
      }
    },
    parseMaybeJson(value) {
      if (typeof value !== 'string') {
        return value;
      }
      try {
        return JSON.parse(value);
      } catch (e) {
        return value;
      }
    },
    getWidgetLabel(widget) {
      const label = (widget && widget.options && (widget.options.label || widget.options.name)) || '字段';
      if (widget && widget.type === 'date-range' && String(label).toLowerCase() === 'date-range') {
        return '日期';
      }
      return label;
    },
    getRawFieldLabel(key) {
      const labelMap = {
        days: '天数',
        day: '天数',
        dayCount: '天数',
        dateDays: '天数',
        leaveDays: '天数',
        totalDays: '天数',
        duration: '天数',
      };
      return labelMap[key] || key;
    },
    ensureDayCountItem(items) {
      const hasDayCount = (items || []).some((item) => {
        const label = String((item && item.label) || '');
        const key = String((item && item.key) || '');
        return /天数|day|days|duration/i.test(label) || /天数|day|days|duration/i.test(key);
      });
      if (hasDayCount) {
        return items;
      }

      const dateRangeIndex = (this.widgets || []).findIndex(widget => widget && widget.type === 'date-range');
      if (dateRangeIndex === -1) {
        return items;
      }

      const nextItems = [...items];
      nextItems.splice(dateRangeIndex + 1, 0, {
        key: 'derived-day-count',
        label: '天数',
        required: false,
        displayType: 'input',
        value: this.getDayCountValue(),
      });
      return nextItems;
    },
    getDayCountValue() {
      const valueKeys = [
        'days',
        'day',
        'dayCount',
        'dateDays',
        'leaveDays',
        'totalDays',
        'duration',
      ];
      const matchedKey = valueKeys.find(key => this.lfFields && this.lfFields[key] !== undefined && this.lfFields[key] !== null && this.lfFields[key] !== '');
      if (matchedKey) {
        return this.stringifyValue(this.lfFields[matchedKey]);
      }

      const dateRangeWidget = (this.widgets || []).find(widget => widget && widget.type === 'date-range');
      const rangeValue = this.getDateRangeParts(dateRangeWidget);
      if (rangeValue.startDate && rangeValue.endDate) {
        const startTime = this.toDateTime(rangeValue.startDate);
        const endTime = this.toDateTime(rangeValue.endDate);
        if (startTime !== null && endTime !== null && endTime >= startTime) {
          const dayCount = Math.ceil((endTime - startTime) / (24 * 60 * 60 * 1000)) + 1;
          return String(dayCount);
        }
      }

      return '';
    },
    getFieldDisplayValue(widget) {
      if (!widget || !widget.options || !widget.options.name) {
        return '';
      }
      const fieldName = widget.options.name;
      if (widget.type === 'date-range') {
        return this.getDateRangeText(widget);
      }
      if (widget.type === 'select') {
        return this.getSelectedLabel(widget);
      }
      return this.stringifyValue(this.formModel[fieldName]);
    },
    getInputType(widget) {
      if (!widget) return 'text';
      if (widget.type === 'number') return 'number';
      if (widget.type === 'date') return 'date';
      return 'text';
    },
    getDateRangeText(widget) {
      const rangeValue = this.getDateRangeParts(widget);
      if (rangeValue.startDate || rangeValue.endDate) {
        return [rangeValue.startDate, rangeValue.endDate].filter(Boolean).join(' - ');
      }
      return '';
    },
    getDateRangeParts(widget) {
      const fieldName = widget && widget.options && widget.options.name;
      const value = fieldName ? this.formModel[fieldName] : '';
      if (Array.isArray(value)) {
        return {
          startDate: value[0] || '',
          endDate: value[1] || '',
        };
      }
      if (value && typeof value === 'object') {
        return {
          startDate: value.startDate || value.start || '',
          endDate: value.endDate || value.end || '',
        };
      }
      if (typeof value === 'string' && value) {
        const parts = value.split(/\s+-\s+|~|至|,/).map(item => item.trim()).filter(Boolean);
        return {
          startDate: parts[0] || '',
          endDate: parts[1] || '',
        };
      }
      return {
        startDate: '',
        endDate: '',
      };
    },
    toDateTime(value) {
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value.getTime();
      }
      if (typeof value === 'number') {
        return Number.isNaN(value) ? null : value;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        const normalized = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
          ? `${trimmed}T00:00:00`
          : trimmed.replace(' ', 'T');
        const date = new Date(normalized);
        return Number.isNaN(date.getTime()) ? null : date.getTime();
      }
      return null;
    },
    getNormalizedOptions(widget) {
      if (!widget || !widget.options) return [];
      return widget.options.options || widget.options.optionItems || [];
    },
    getOptValue(opt) {
      return opt && typeof opt === 'object' ? opt.value : opt;
    },
    getOptLabel(opt) {
      return opt && typeof opt === 'object' ? opt.label : opt;
    },
    getSelectedLabel(widget) {
      if (!widget || !widget.options) {
        return '';
      }
      const fieldName = widget.options.name;
      const currentValue = this.formModel[fieldName];
      const option = this.getNormalizedOptions(widget).find((opt) => {
        if (opt && typeof opt === 'object') {
          return opt.value === currentValue || opt.label === currentValue;
        }
        return opt === currentValue;
      });
      if (option) {
        return this.getOptLabel(option);
      }
      return this.stringifyValue(currentValue);
    },
    zoomOut() {
      this.flowScale = Math.max(0.3, Number((this.flowScale - 0.1).toFixed(2)));
    },
    zoomIn() {
      this.flowScale = Math.min(1, Number((this.flowScale + 0.1).toFixed(2)));
    },
    resetZoom() {
      this.flowScale = 0.7;
    },
    getActionText(operationType) {
      const map = {
        3: '同意',
        4: '不同意',
        18: '退回修改',
        21: '转办',
      };
      return map[operationType] || '审批';
    },
    getButtonClass(operationType) {
      const map = {
        3: 'btn-agree',
        4: 'btn-disagree',
        18: 'btn-return',
        21: 'btn-transfer',
      };
      return map[operationType] || 'btn-default';
    },
    getRecordClass(operation) {
      const text = String(operation || '');
      if (text.indexOf('不同意') !== -1 || text.indexOf('拒绝') !== -1 || text.indexOf('退回') !== -1) {
        return 'status-danger';
      }
      if (text.indexOf('提交') !== -1) {
        return 'status-primary';
      }
      if (text.indexOf('同意') !== -1 || text.indexOf('通过') !== -1) {
        return 'status-success';
      }
      if (text.indexOf('结束') !== -1) {
        return 'status-default';
      }
      return 'status-primary';
    },
    cleanDisplayText(text) {
      return String(text || '').trim();
    },
    getDefaultNodeName(index) {
      const names = ['发起人', '审核人', '流程结束'];
      return names[index] || `节点${index + 1}`;
    },
    stringifyValue(value) {
      if (value === undefined || value === null || value === '') return '-';
      if (Array.isArray(value)) return value.join('、');
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    },
    hiddenCode(code) {
      if (!code) return '--';
      const value = String(code);
      if (value.length <= 10) return value;
      return `${value.slice(0, 4)}****${value.slice(-4)}`;
    },
    formatTime(value) {
      if (!value) return '--';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hour}:${minute}`;
    },
    goTaskList() {
      this.$router.push({
        path: '/home/oa/tasks',
        query: { mode: this.taskInfo.entryType || 'todo' },
      });
    },
  },
};
</script>

<style scoped>
.oa-approve-page {
  height: 100%;
  padding: 16px 24px 88px;
  background: #f5f5f7;
  box-sizing: border-box;
  overflow-y: auto;
}

.page-header-card,
.summary-card,
.detail-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.page-header-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  margin-bottom: 16px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.page-subtitle {
  margin-top: 4px;
  font-size: 13px;
  color: #909399;
}

.page-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.summary-card {
  padding: 18px 20px;
  margin-bottom: 16px;
}

.summary-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 14px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 20px;
}

.summary-item {
  font-size: 14px;
  color: #909399;
}

.summary-item strong {
  color: #303133;
  font-weight: 500;
}

.detail-card {
  padding: 0 18px 18px;
}

.tabs {
  display: flex;
  gap: 20px;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 18px;
}

.tab-btn {
  height: 48px;
  border: none;
  background: transparent;
  color: #606266;
  font-size: 15px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}

.tab-btn.active {
  color: #1976d2;
  border-bottom-color: #1976d2;
  font-weight: 600;
}

.loading-state,
.empty-state {
  padding: 42px 16px;
  text-align: center;
  color: #909399;
  background: #fafafa;
  border-radius: 10px;
}

.form-grid {
  display: block;
  max-width: 1120px;
  padding: 36px 24px 120px;
}

.form-item {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  align-items: center;
  gap: 18px;
  min-width: 0;
  margin-bottom: 22px;
}

.field-label {
  display: block;
  margin-bottom: 0;
  color: #303133;
  font-size: 15px;
  font-weight: 600;
  text-align: right;
}

.required {
  color: #f56c6c;
  margin-left: 4px;
}

.field-input,
.field-textarea,
.field-select {
  width: 100%;
  box-sizing: border-box;
  min-height: 44px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  background: #f7f8fb;
  color: #606266;
  padding: 0 14px;
  font-size: 15px;
  text-align: center;
  text-align-last: center;
}

.field-textarea {
  min-height: 92px;
  padding: 10px 14px;
  text-align: left;
  resize: vertical;
}

.raw-field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.raw-field {
  padding: 12px;
  border-radius: 10px;
  background: #f9fafc;
}

.raw-field span {
  display: block;
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.raw-field strong {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
  word-break: break-all;
}

.record-table {
  border: 1px solid #ebeef5;
  border-radius: 10px;
  overflow: hidden;
}

.record-row {
  display: grid;
  grid-template-columns: 1.2fr 1fr 0.8fr 1.4fr 1.2fr;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid #ebeef5;
  font-size: 13px;
  color: #606266;
}

.record-row:last-child {
  border-bottom: none;
}

.record-head {
  background: #f5f7fa;
  color: #303133;
  font-weight: 600;
}

.record-status {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-style: normal;
  font-size: 12px;
}

.status-primary {
  background: #ecf5ff;
  color: #1976d2;
}

.status-success {
  background: #f0f9eb;
  color: #67c23a;
}

.status-danger {
  background: #fef0f0;
  color: #f56c6c;
}

.status-default {
  background: #f4f4f5;
  color: #909399;
}

.flow-panel {
  position: relative;
  min-height: 360px;
  padding-top: 20px;
}

.flow-zoom-controls {
  position: absolute;
  top: 0;
  right: 12px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 10px;
}

.zoom-btn {
  min-width: 34px;
  height: 34px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #303133;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}

.zoom-btn:hover {
  color: #1976d2;
  border-color: #1976d2;
}

.reset-btn {
  font-size: 14px;
}

.zoom-text {
  min-width: 46px;
  color: #303133;
  font-size: 15px;
  text-align: center;
}

.flow-preview {
  max-width: 700px;
  margin: 0 auto;
  padding: 10px 0 36px;
}

.flow-node-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.flow-node {
  width: var(--flow-node-width);
  min-height: var(--flow-node-min-height);
  overflow: hidden;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background: #fff;
  text-align: center;
  box-shadow: 0 7px 15px rgba(31, 45, 61, 0.12);
}

.flow-node.is-current {
  border-color: #1976d2;
  border-width: 2px;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.12), 0 7px 15px rgba(31, 45, 61, 0.14);
}

.flow-node.is-passed {
  border-color: #e4e7ed;
}

.flow-node.is-future {
  opacity: 0.72;
}

.node-title {
  height: var(--flow-title-height);
  line-height: var(--flow-title-height);
  padding: 0 var(--flow-title-padding);
  background: #5b6f98;
  color: #fff;
  font-size: var(--flow-title-font-size);
  font-weight: 500;
  text-align: left;
}

.flow-node.is-start .node-title {
  background: #5b6f98;
}

.flow-node.is-approver .node-title {
  background: #ff9636;
}

.flow-node.is-current .node-title {
  background: #ff9636;
}

.node-meta {
  min-height: var(--flow-meta-min-height);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--flow-meta-padding-y) var(--flow-meta-padding-x);
  font-size: var(--flow-meta-font-size);
  color: #303133;
}

.flow-line {
  position: relative;
  width: var(--flow-line-width);
  height: var(--flow-line-height);
  background: #dcdfe6;
}

.flow-line::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -1px;
  width: 0;
  height: 0;
  transform: translateX(-50%);
  border-left: var(--flow-arrow-size) solid transparent;
  border-right: var(--flow-arrow-size) solid transparent;
  border-top: var(--flow-arrow-height) solid #dcdfe6;
}

.flow-end-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.flow-end-line {
  width: var(--flow-line-width);
  height: var(--flow-line-height);
  background: #dcdfe6;
}

.flow-end-dot {
  width: var(--flow-dot-size);
  height: var(--flow-dot-size);
  border-radius: 50%;
  background: #d9d9d9;
}

.flow-end-text {
  margin-top: var(--flow-end-margin);
  color: #909399;
  font-size: var(--flow-end-font-size);
}

.approval-bar {
  position: fixed;
  right: 24px;
  bottom: 20px;
  left: 196px;
  z-index: 10;
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
}

.ghost-btn,
.approval-btn {
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ghost-btn {
  height: 40px;
  padding: 0 16px;
  font-size: 14px;
  background: #f4f4f5;
  color: #606266;
  border: 1px solid #dcdfe6;
}

.ghost-btn:hover {
  color: #1976d2;
  border-color: #1976d2;
  background: #ecf5ff;
}

.approval-btn {
  min-width: auto;
  height: 38px;
  color: #fff;
  padding: 0 28px;
  font-size: 14px;
}

.btn-return {
  padding: 0 34px;
}

.approval-btn:disabled {
  cursor: not-allowed;
  opacity: 0.68;
}

.btn-agree {
  background: #67c23a;
}

.btn-disagree {
  background: #f56c6c;
}

.btn-return {
  background: #e6a23c;
}

.btn-transfer {
  background: #1976d2;
}

.btn-default {
  background: #1976d2;
}

.dialog-mask {
  position: fixed;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.36);
}

.transfer-dialog {
  width: 420px;
  max-width: calc(100vw - 40px);
  border-radius: 12px;
  background: #fff;
  padding: 20px;
  box-shadow: 0 16px 42px rgba(0, 0, 0, 0.18);
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
}

.dialog-label {
  margin-top: 14px;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

@media (max-width: 900px) {
  .summary-grid,
  .raw-field-grid {
    grid-template-columns: 1fr;
  }

  .form-grid {
    padding: 24px 0 90px;
  }

  .form-item {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .field-label {
    text-align: left;
  }

  .record-row {
    grid-template-columns: 1fr;
  }

  .approval-bar {
    left: 24px;
  }
}
</style>
